import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Fade, Slide, Zoom } from "react-awesome-reveal";
import {
  FaCalendarAlt,
  FaChevronRight,
  FaClipboardList,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
  FaMedal,
  FaTrophy,
} from "react-icons/fa";

import { getAdherentsByUser } from "../../services/adherentServices";
import {
  getEventsByAdherent,
  getHomeDemoResults,
} from "../../services/eventServices";

const MAX_RESULTS_HOME = 3;

const RESULT_STYLES = [
  {
    gradient: "from-amber-500 via-orange-600 to-slate-950",
    soft: "from-amber-500/15 via-orange-500/10 to-transparent",
    chip: "bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-200",
    accent: "text-orange-500",
  },
  {
    gradient: "from-sky-600 via-blue-700 to-slate-950",
    soft: "from-sky-500/15 via-blue-500/10 to-transparent",
    chip: "bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200",
    accent: "text-sky-600 dark:text-sky-300",
  },
  {
    gradient: "from-emerald-600 via-teal-700 to-slate-950",
    soft: "from-emerald-500/15 via-teal-500/10 to-transparent",
    chip:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200",
    accent: "text-emerald-600 dark:text-emerald-300",
  },
];

const normalizeEventsResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Events)) return response.Events;
  if (Array.isArray(response?.events)) return response.events;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.Events)) return response.data.Events;
  if (Array.isArray(response?.data?.events)) return response.data.events;
  return [];
};

const normalizeAdherentsResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.adherents)) return response.adherents;
  if (Array.isArray(response?.Adherents)) return response.Adherents;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.adherents)) return response.data.adherents;
  return [];
};

const getEventStart = (event) =>
  event?.start || event?.date_event || event?.date;

const getEventLocation = (event) =>
  event?.lieu_event || event?.lieu || "Lieu non précisé";

const getEventCover = (event) =>
  event?.photo_url ||
  event?.image_url ||
  event?.photo ||
  event?.cover_url ||
  "/img/cover/result_home.webp";

const getAdherentEvent = (event) =>
  event?.AdherentEvent ||
  event?.adherentEvent ||
  event?.Adherents?.[0]?.AdherentEvent ||
  {};

const cleanText = (value) => {
  if (!value) return "";
  return String(value).replace(/<[^>]*>/g, "").trim();
};

const hasValue = (value) => cleanText(value).length > 0;

const hasResultData = (event) => {
  const link = getAdherentEvent(event);

  return (
    hasValue(link.resultats) ||
    hasValue(link.classement) ||
    hasValue(link.observations)
  );
};

const isValidDate = (dateValue) => {
  const date = new Date(dateValue);
  return Number.isFinite(date.getTime());
};

const getTimestamp = (dateValue) => {
  const date = new Date(dateValue);
  return Number.isFinite(date.getTime()) ? date.getTime() : 0;
};

const getLatestResults = (events) =>
  [...events]
    .filter(hasResultData)
    .sort(
      (a, b) =>
        getTimestamp(getEventStart(b)) - getTimestamp(getEventStart(a)),
    )
    .slice(0, MAX_RESULTS_HOME);

const dedupeResults = (events) => {
  const map = new Map();

  events.forEach((event) => {
    const link = getAdherentEvent(event);
    const key = `${event?.id || event?.titre}-${link?.adherent_id || ""}`;

    if (key && !map.has(key)) {
      map.set(key, event);
    }
  });

  return Array.from(map.values());
};

const formatDate = (dateValue) => {
  if (!isValidDate(dateValue)) return "Date non précisée";

  return new Date(dateValue).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const formatDay = (dateValue) => {
  if (!isValidDate(dateValue)) return "--";

  return new Date(dateValue).toLocaleDateString("fr-FR", {
    day: "2-digit",
  });
};

const formatMonth = (dateValue) => {
  if (!isValidDate(dateValue)) return "---";

  return new Date(dateValue).toLocaleDateString("fr-FR", {
    month: "short",
  });
};

const getAdherentLabel = (event) => {
  const adherent = event?._homeAdherent;

  if (!adherent?.prenom && !adherent?.nom) return "";

  return [adherent.prenom, adherent.nom].filter(Boolean).join(" ");
};

const getAdherentOptionLabel = (adherent) => {
  if (!adherent?.prenom && !adherent?.nom) {
    return `Adhérent #${adherent?.id || ""}`;
  }

  return [adherent.prenom, adherent.nom].filter(Boolean).join(" ");
};

function ResultsSkeleton() {
  return (
    <div className="grid gap-5 lg:grid-cols-3">
      {[1, 2, 3].map((item) => (
        <div
          key={item}
          className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20"
        >
          <div className="h-52 animate-pulse rounded-[1.5rem] bg-slate-200 dark:bg-white/10" />

          <div className="space-y-3 p-3 pt-5">
            <div className="h-4 w-24 animate-pulse rounded-full bg-slate-200 dark:bg-white/10" />
            <div className="h-5 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-white/10" />
            <div className="h-4 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-white/10" />
            <div className="h-20 w-full animate-pulse rounded-2xl bg-slate-200 dark:bg-white/10" />
          </div>
        </div>
      ))}
    </div>
  );
}

function AdherentSelect({
  isConnected,
  adherents,
  selectedAdherentId,
  onChange,
}) {
  if (!isConnected || adherents.length <= 1) return null;

  return (
    <Fade direction="up" triggerOnce delay={180}>
      <div className="rounded-[1.4rem] border border-slate-200 bg-white p-3 shadow-sm dark:border-white/10 dark:bg-white/[0.06]">
        <label
          htmlFor="results-adherent"
          className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400"
        >
          Judoka affiché
        </label>

        <select
          id="results-adherent"
          value={selectedAdherentId}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-11 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-bold text-slate-800 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10 dark:border-white/10 dark:bg-slate-950 dark:text-white"
        >
          {adherents.map((adherent) => (
            <option key={adherent.id} value={String(adherent.id)}>
              {getAdherentOptionLabel(adherent)}
            </option>
          ))}
        </select>
      </div>
    </Fade>
  );
}

function ResultsEmptyState({ isConnected, onAction, hasAdherents }) {
  return (
    <Fade direction="up" triggerOnce>
      <div className="overflow-hidden rounded-[2.2rem] border border-slate-200 bg-white p-3 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
        <div className="grid gap-5 rounded-[1.8rem] bg-slate-50 p-4 dark:bg-white/[0.04] md:grid-cols-[0.85fr_1.15fr] md:items-center md:p-5">
          <div className="relative min-h-64 overflow-hidden rounded-[1.55rem] bg-slate-900">
            <img
              src="/img/cover/result_home.webp"
              alt="Résultats Judo Coach Pro"
              className="absolute inset-0 h-full w-full object-cover opacity-80 transition duration-700 hover:scale-105"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/35 to-transparent" />

            <div className="absolute bottom-4 left-4 right-4 text-white">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] backdrop-blur-xl">
                <FaTrophy />
                Résultats
              </div>

              <p className="mt-3 font-heading text-2xl font-bold leading-tight">
                {isConnected
                  ? hasAdherents
                    ? "Les résultats de ce judoka apparaîtront ici"
                    : "Aucun judoka associé au compte"
                  : "Aucun résultat de démonstration"}
              </p>
            </div>
          </div>

          <div className="px-1 py-2 md:px-3">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 dark:text-orange-300">
              {isConnected ? "Aucun résultat" : "Compte démo"}
            </p>

            <h3 className="mt-3 font-heading text-2xl font-bold leading-tight text-slate-950 dark:text-white sm:text-3xl">
              {isConnected
                ? hasAdherents
                  ? "Les derniers résultats de ce judoka seront mis en avant automatiquement."
                  : "Ajoutez d’abord un adhérent pour suivre ses résultats."
                : "Ajoutez quelques résultats au compte démo pour valoriser cette section."}
            </h3>

            <p className="mt-4 max-w-xl text-sm font-medium leading-7 text-slate-600 dark:text-white/55 sm:text-base">
              Classements, observations et bilans de compétition restent
              accessibles depuis votre espace résultats.
            </p>

            <button
              type="button"
              onClick={onAction}
              className="mt-6 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-yellow-400 px-6 py-3 text-sm font-bold text-slate-950 shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 active:translate-y-0 sm:w-auto font-ui"
            >
              {isConnected ? "Voir mes résultats" : "Créer mon compte"}
              <FaChevronRight className="text-xs" />
            </button>
          </div>
        </div>
      </div>
    </Fade>
  );
}

function ResultCard({ event, index }) {
  const startDate = getEventStart(event);
  const location = getEventLocation(event);
  const link = getAdherentEvent(event);
  const style = RESULT_STYLES[index % RESULT_STYLES.length];
  const adherentLabel = getAdherentLabel(event);

  const classement = cleanText(link?.classement);
  const resultats = cleanText(link?.resultats);
  const observations = cleanText(link?.observations);

  return (
    <Zoom triggerOnce delay={index * 80}>
      <article className="group relative h-full overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-xl shadow-slate-200/60 transition duration-300 hover:-translate-y-1 hover:shadow-2xl dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
        <div
          className={`pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-br ${style.soft}`}
        />

        <div className="relative h-52 overflow-hidden rounded-[1.55rem] bg-slate-900">
          <img
            src={getEventCover(event)}
            alt={event?.titre || "Résultat Judo Coach Pro"}
            className="h-full w-full object-cover opacity-85 transition duration-700 group-hover:scale-105"
            loading="lazy"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/25 to-transparent" />

          <div className="absolute left-4 top-4 overflow-hidden rounded-2xl bg-white text-center shadow-lg dark:bg-slate-950">
            <div
              className={`bg-gradient-to-br ${style.gradient} px-4 py-2 text-white`}
            >
              <p className="text-2xl font-bold leading-none">
                {formatDay(startDate)}
              </p>

              <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em]">
                {formatMonth(startDate)}
              </p>
            </div>
          </div>

          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3 text-white">
            <div className="inline-flex min-w-0 items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold backdrop-blur-xl">
              <FaTrophy className="shrink-0" />
              <span className="truncate">
                {event?.type_event || "Compétition"}
              </span>
            </div>

            {classement && (
              <div className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-3 py-2 text-xs font-bold text-slate-950 shadow-lg">
                <FaMedal className="text-orange-500" />
                <span>{classement}</span>
              </div>
            )}
          </div>
        </div>

        <div className="relative p-3 pt-5">
          <div className="flex flex-wrap gap-2">
            {event?.cate_event && (
              <span
                className={`rounded-full px-3 py-1 text-xs font-bold ${style.chip}`}
              >
                {event.cate_event}
              </span>
            )}

            {event?.level_event && (
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-white/60">
                {event.level_event}
              </span>
            )}
          </div>

          <h3 className="mt-4 line-clamp-2 font-heading text-xl font-bold leading-tight text-slate-950 dark:text-white">
            {event?.titre || "Événement sans titre"}
          </h3>

          {adherentLabel && (
            <p className="mt-2 text-sm font-bold text-sky-700 dark:text-sky-200">
              {adherentLabel}
            </p>
          )}

          <div className="mt-4 space-y-2 text-sm font-medium text-slate-600 dark:text-white/55">
            <p className="flex items-center gap-2">
              <FaCalendarAlt className={`shrink-0 ${style.accent}`} />
              <span className="line-clamp-1">{formatDate(startDate)}</span>
            </p>

            <p className="flex items-center gap-2">
              <FaMapMarkerAlt className="shrink-0 text-orange-500" />
              <span className="line-clamp-1">{location}</span>
            </p>
          </div>

          <div className="mt-5 grid gap-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400 dark:text-white/35">
                Résultat
              </p>

              <p className="mt-2 text-sm font-bold leading-6 text-slate-800 dark:text-white/80">
                {resultats || "Résultat non renseigné"}
              </p>
            </div>

            {observations && (
              <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4 dark:border-orange-400/20 dark:bg-orange-500/10">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-orange-700 dark:text-orange-200">
                  Observation du coach
                </p>

                <p className="mt-2 line-clamp-3 text-sm font-medium leading-6 text-orange-950 dark:text-orange-50">
                  {observations}
                </p>
              </div>
            )}
          </div>

          {(link?.podium_url || link?.tableau_url) && (
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              {link?.podium_url && (
                <a
                  href={link.podium_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-white/[0.07] dark:text-white dark:hover:bg-white/10"
                >
                  Podium
                  <FaExternalLinkAlt className="text-xs" />
                </a>
              )}

              {link?.tableau_url && (
                <a
                  href={link.tableau_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-white/[0.07] dark:text-white dark:hover:bg-white/10"
                >
                  Tableau
                  <FaExternalLinkAlt className="text-xs" />
                </a>
              )}
            </div>
          )}
        </div>
      </article>
    </Zoom>
  );
}

function ResultatsHome({ isConnected }) {
  const navigate = useNavigate();

  const [managedAdherents, setManagedAdherents] = useState([]);
  const [selectedAdherentId, setSelectedAdherentId] = useState("");
  const [adherentsLoaded, setAdherentsLoaded] = useState(!isConnected);

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  const selectedAdherent = useMemo(() => {
    if (!selectedAdherentId) return null;

    return (
      managedAdherents.find(
        (adherent) => String(adherent.id) === String(selectedAdherentId),
      ) || null
    );
  }, [managedAdherents, selectedAdherentId]);

  const sectionContent = useMemo(() => {
    if (isConnected) {
      return {
        eyebrow: "Mes résultats",
        title: "Les résultats du judoka.",
        subtitle:
          "Ce composant affiche le premier judoka géré. Vous pouvez basculer sur un autre profil.",
        note: null,
        primaryLabel: "Voir tous mes résultats",
        primaryPath: "/resultats",
      };
    }

    return {
      eyebrow: "Suivi des résultats",
      title: "Chaque compétition laisse une trace utile.",
      subtitle:
        "Judo Coach Pro permet de suivre les résultats, les classements et les observations du coach tout au long de la saison.",
      note: "Démonstration : les résultats ci-dessous proviennent du compte de démonstration.",
      primaryLabel: "Créer mon compte gratuit",
      primaryPath: "/register",
    };
  }, [isConnected]);

  const handleNavigation = () => {
    navigate(sectionContent.primaryPath);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchManagedAdherents = async () => {
      if (!isConnected) {
        if (isMounted) {
          setManagedAdherents([]);
          setSelectedAdherentId("");
          setAdherentsLoaded(true);
        }

        return;
      }

      setAdherentsLoaded(false);

      try {
        const response = await getAdherentsByUser();
        const adherents = normalizeAdherentsResponse(response);

        if (!isMounted) return;

        setManagedAdherents(adherents);

        setSelectedAdherentId((currentId) => {
          const currentStillExists = adherents.some(
            (adherent) => String(adherent.id) === String(currentId),
          );

          if (currentId && currentStillExists) {
            return currentId;
          }

          return adherents[0]?.id ? String(adherents[0].id) : "";
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des adhérents :", error);

        if (isMounted) {
          setManagedAdherents([]);
          setSelectedAdherentId("");
        }
      } finally {
        if (isMounted) {
          setAdherentsLoaded(true);
        }
      }
    };

    fetchManagedAdherents();

    return () => {
      isMounted = false;
    };
  }, [isConnected]);

  useEffect(() => {
    let isMounted = true;

    const fetchSelectedAdherentResults = async () => {
      if (!selectedAdherentId) return [];

      const response = await getEventsByAdherent(selectedAdherentId);
      const events = normalizeEventsResponse(response);

      return dedupeResults(
        events.map((event) => ({
          ...event,
          _homeAdherent: selectedAdherent
            ? {
                id: selectedAdherent.id,
                nom: selectedAdherent.nom,
                prenom: selectedAdherent.prenom,
              }
            : null,
        })),
      );
    };

    const fetchVisitorResults = async () => {
      const response = await getHomeDemoResults();
      return normalizeEventsResponse(response);
    };

    const fetchResults = async () => {
      if (isConnected && !adherentsLoaded) {
        setLoading(true);
        return;
      }

      if (isConnected && !selectedAdherentId) {
        setResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const loadedResults = isConnected
          ? await fetchSelectedAdherentResults()
          : await fetchVisitorResults();

        if (isMounted) {
          setResults(getLatestResults(loadedResults));
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des résultats :", error);

        if (isMounted) {
          setResults([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchResults();

    return () => {
      isMounted = false;
    };
  }, [isConnected, adherentsLoaded, selectedAdherentId, selectedAdherent]);

  const hasResults = results.length > 0;
  const hasAdherents = managedAdherents.length > 0;

  return (
    <section className="relative overflow-hidden bg-white px-4 py-16 text-slate-950 dark:bg-[#07111f] dark:text-white sm:px-6 sm:py-20 lg:px-8">
      <div className="absolute -left-24 top-20 h-72 w-72 rounded-full bg-orange-500/10 blur-3xl" />
      <div className="absolute right-0 top-0 h-80 w-80 rounded-full bg-sky-500/10 blur-3xl dark:bg-sky-400/10" />
      <div className="absolute bottom-0 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-400/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl">
        <div className="mb-9 grid gap-6 lg:grid-cols-[0.68fr_0.32fr] lg:items-end">
          <Slide direction="left" triggerOnce>
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] text-orange-700 shadow-sm dark:border-orange-400/20 dark:bg-orange-500/10 dark:text-orange-200">
                <FaClipboardList />
                {sectionContent.eyebrow}
              </div>

              <h2 className="mt-5 font-heading text-3xl font-bold leading-tight text-slate-950 dark:text-white sm:text-4xl lg:text-5xl">
                {sectionContent.title}
              </h2>

              <p className="mt-4 max-w-2xl text-base font-medium leading-8 text-slate-600 dark:text-white/55 sm:text-lg">
                {sectionContent.subtitle}
              </p>
            </div>
          </Slide>

          <div className="grid gap-3">
            <AdherentSelect
              isConnected={isConnected}
              adherents={managedAdherents}
              selectedAdherentId={selectedAdherentId}
              onChange={setSelectedAdherentId}
            />

            <Fade direction="up" triggerOnce delay={120}>
              <div className="flex lg:justify-end">
                <button
                  type="button"
                  onClick={handleNavigation}
                  className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-yellow-400 px-6 py-3 text-sm font-bold text-slate-950 shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 active:translate-y-0 sm:w-auto lg:w-full"
                >
                  {sectionContent.primaryLabel}
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            </Fade>
          </div>
        </div>

        {sectionContent.note && (
          <Fade direction="up" triggerOnce>
            <div className="mb-6 rounded-[1.5rem] border border-orange-200 bg-orange-50 p-4 text-sm font-semibold leading-6 text-orange-800 dark:border-orange-400/20 dark:bg-orange-500/10 dark:text-orange-100">
              {sectionContent.note}
            </div>
          </Fade>
        )}

        {loading && <ResultsSkeleton />}

        {!loading && !hasResults && (
          <ResultsEmptyState
            isConnected={isConnected}
            hasAdherents={hasAdherents}
            onAction={handleNavigation}
          />
        )}

        {!loading && hasResults && (
          <div className="grid gap-5 lg:grid-cols-3">
            {results.map((event, index) => (
              <ResultCard
                key={`${event?.id || event?.titre}-${
                  getAdherentEvent(event)?.adherent_id || index
                }`}
                event={event}
                index={index}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default ResultatsHome;